import { addDays, differenceInDays, parseCivilDate } from './civil-date.js';
export const MATCH_WINDOW_DAYS = 5;
export const MATCH_TOLERANCE_PERCENT = 15;
export const BANK_WAIT_DAYS = 3;
export const RETURN_WINDOW_DAYS = 3;
const PERCENT = 100n;
const STATUS_RANK = {
    planned: 0,
    paid: 1,
    paused: 2,
    cancelled: 3,
};
const absolute = (value) => (value < 0n ? -value : value);
const compareBigInts = (left, right) => {
    if (left === right) {
        return 0;
    }
    return left < right ? -1 : 1;
};
const compareTexts = (left, right) => {
    if (left === right) {
        return 0;
    }
    return left < right ? -1 : 1;
};
const bankWaitDaysOf = (graceDays) => Math.max(graceDays + BANK_WAIT_DAYS, MATCH_WINDOW_DAYS);
export const lastDueDatePastBankWait = (today, graceDays) => addDays(today, -bankWaitDaysOf(graceDays) - 1);
export const paidMoneyOf = (operation, currency) => {
    const isPaidInPurchaseCurrency = operation.purchase !== null && operation.purchase.currency === currency;
    if (isPaidInPurchaseCurrency) {
        return operation.purchase;
    }
    return operation.charged.currency === currency ? operation.charged : null;
};
export const isWithinTolerance = (actualMinor, expectedMinor) => {
    const gap = absolute(BigInt(actualMinor) - BigInt(expectedMinor));
    return gap * PERCENT <= BigInt(expectedMinor) * BigInt(MATCH_TOLERANCE_PERCENT);
};
export const isMatchable = (period) => {
    const isPlanned = period.status === 'planned';
    const isPaidWithoutOperation = period.status === 'paid' && !period.isMatched;
    return isPlanned || isPaidWithoutOperation;
};
const isLinkedPair = (links, paymentId, merchantKey) => links.some((link) => link.paymentId === paymentId && link.merchantKey === merchantKey);
const isNewPriceOf = (period, paid) => {
    const expected = period.amountMinor;
    if (period.isVariable || expected === null) {
        return false;
    }
    return !isWithinTolerance(paid.amountMinor, expected);
};
const pairOf = (operation, period) => {
    const dayGap = Math.abs(differenceInDays(parseCivilDate(period.dueDate), parseCivilDate(operation.occurredOn)));
    const paid = paidMoneyOf(operation, period.currency);
    const isOutsideWindow = dayGap > MATCH_WINDOW_DAYS;
    if (isOutsideWindow || paid === null) {
        return null;
    }
    return { operation, period, paid, dayGap, isNewPrice: isNewPriceOf(period, paid) };
};
const paymentsInWindowOf = (pairs, operationId) => new Set(pairs.filter((pair) => pair.operation.id === operationId).map((pair) => pair.period.paymentId)).size;
const isAmountAccepted = (pair, pairs) => !pair.isNewPrice || paymentsInWindowOf(pairs, pair.operation.id) === 1;
const amountGapOf = (pair) => {
    const expected = pair.period.amountMinor;
    if (pair.period.isVariable || expected === null) {
        return { gap: 0n, base: 1n };
    }
    return {
        gap: absolute(BigInt(pair.paid.amountMinor) - BigInt(expected)),
        base: BigInt(expected),
    };
};
const compareAmountGaps = (left, right) => {
    const leftGap = amountGapOf(left);
    const rightGap = amountGapOf(right);
    return compareBigInts(leftGap.gap * rightGap.base, rightGap.gap * leftGap.base);
};
const PAIR_ORDER = [
    (left, right) => Number(left.isNewPrice) - Number(right.isNewPrice),
    (left, right) => Number(left.period.isVariable) - Number(right.period.isVariable),
    compareAmountGaps,
    (left, right) => left.dayGap - right.dayGap,
    (left, right) => STATUS_RANK[left.period.status] - STATUS_RANK[right.period.status],
    (left, right) => compareTexts(left.period.dueDate, right.period.dueDate),
    (left, right) => compareTexts(left.operation.occurredOn, right.operation.occurredOn),
    (left, right) => compareTexts(left.operation.id, right.operation.id),
    (left, right) => compareTexts(left.period.id, right.period.id),
];
const comparePairs = (left, right) => PAIR_ORDER.reduce((decided, compare) => (decided === 0 ? compare(left, right) : decided), 0);
const assignInOrder = (pairs) => [...pairs].sort(comparePairs).reduce((assignment, pair) => {
    const isOperationTaken = assignment.operationIds.has(pair.operation.id);
    const isTaken = isOperationTaken || assignment.periodIds.has(pair.period.id);
    if (isTaken) {
        return assignment;
    }
    return {
        taken: [...assignment.taken, pair],
        operationIds: new Set([...assignment.operationIds, pair.operation.id]),
        periodIds: new Set([...assignment.periodIds, pair.period.id]),
    };
}, { taken: [], operationIds: new Set(), periodIds: new Set() }).taken;
export const assignMatches = (operations, periods, links) => {
    const matchable = periods.filter(isMatchable);
    const inWindow = operations.flatMap((operation) => matchable
        .filter((period) => isLinkedPair(links, period.paymentId, operation.merchantKey))
        .map((period) => pairOf(operation, period))
        .filter((pair) => pair !== null));
    const pairs = inWindow.filter((pair) => isAmountAccepted(pair, inWindow));
    return assignInOrder(pairs).map((pair) => ({
        operationId: pair.operation.id,
        periodId: pair.period.id,
        paid: pair.paid,
        isNewPrice: pair.isNewPrice,
    }));
};
//# sourceMappingURL=subscription-match.js.map